import type { AuthenticatedUser } from "@shared/schemas/entities";

import {
  belongsToCurrentImpersonation,
  readImpersonate,
} from "./impersonateStore";

// ---------------------------------------------------------------------------
// Last-Known-User-Cache (Session 16, Tab-Wake-Flash-Fix).
// ---------------------------------------------------------------------------
// Persistiert das zuletzt erfolgreich von /api/me geladene User-Objekt in
// localStorage. Beim naechsten Tab-Wake / Page-Reload kann der AuthProvider
// synchron mit "signed-in" + dem cached User starten — ohne Dauer-„Laden..."-
// Flash. fetchMe() laeuft im Hintergrund und korrigiert den User wenn sich
// etwas geaendert hat (z.B. neue Rolle).
//
// Risiko: der User sieht eine Sekunde lang die App, obwohl seine Supabase-
// Session inzwischen abgelaufen ist. Der Fallback wirft ihn dann auf /login.
// Akzeptabel — kein Datenleck, weil das echte /api/me ohne gueltigen JWT
// 401 returnt und der Catch-Branch in AuthContext den User clearet.

// ---------------------------------------------------------------------------
// VTR-7 — auch diese Spur gehoert allen Tabs, die Vertretung aber nur einem
// ---------------------------------------------------------------------------
// STABIL-14 hat ZWEI kennungslose localStorage-Spuren benannt, die der
// optimistische Start synchron liest: der gespiegelte Abfrage-Cache und diese
// hier. Die erste traegt seit VTR-7 das Paar; ohne dieselbe Behandlung hier
// bliebe die Haelfte offen, um die es Daniel ging — der NAME.
//
// Gemessen im Waechter (zweiter Tab, `/api/me` noch unterwegs): vorher stand
// dort «Sandro Kraettli · 129 invoice_overdue», also fremder Name UND fremde
// Karten. Nach dem Cache-Fix allein blieb «Sandro Kraettli · 1
// it_alarm_check_failed» — die Karten gehoerten wieder dem Richtigen, der Name
// noch nicht. Deshalb wandert der Eintrag jetzt zusammen mit der Vertretung,
// unter der er entstanden ist.
//
// Passt sie nicht, gilt der Eintrag als nicht vorhanden: dann laedt die Seite
// kurz, statt einen fremden Namen zu zeigen. Ein Ladebalken ist die bessere
// Luege.
const KEY = "bergler.last-known-user";

/**
 * Was unter `KEY` liegt: der Mensch UND die Vertretung, unter der er notiert
 * wurde. Beides in EINEM Schreibvorgang — getrennt gespeichert driften sie
 * auseinander, und genau daran ist STABIL-13 gescheitert.
 */
interface StoredLastKnownUser {
  user: AuthenticatedUser;
  /** Wen dieser Tab vertrat, als der Eintrag entstand. `null` = niemanden. */
  vertretenerId: string | null;
}

export function readLastKnownUser(): AuthenticatedUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as unknown;
    // Ein Eintrag aus der Zeit vor VTR-7 hat die Huelle nicht und kann seine
    // Vertretung nicht belegen — er wird verworfen. Einmalig, fail-safe, und
    // beim naechsten `/api/me` steht er in neuer Form wieder da.
    if (
      typeof stored !== "object" ||
      stored === null ||
      !("user" in stored)
    ) {
      return null;
    }
    const { user: parsed, vertretenerId } = stored as StoredLastKnownUser;
    if (
      !belongsToCurrentImpersonation(
        typeof vertretenerId === "string" ? vertretenerId : null,
      )
    ) {
      return null;
    }
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as { id?: unknown }).id === "string" &&
      typeof (parsed as { email?: unknown }).email === "string" &&
      typeof (parsed as { role?: unknown }).role === "string" &&
      // LEAD-8: das Merkmal MUSS im Speicher stehen, sonst waere `user`
      // zwar vom Typ `AuthenticatedUser`, traege aber `undefined` in einem
      // `boolean`-Feld — und `canDistributeLeads` gaebe `undefined` zurueck.
      // Ein Eintrag von vor diesem Deploy ist damit kein gueltiger Cache
      // mehr: einmal der normale Lade-Pfad, danach steht er wieder drin.
      typeof (parsed as { mayDistributeLeads?: unknown })
        .mayDistributeLeads === "boolean" &&
      // LEAD-10: dasselbe fuer den zweiten Traeger. Ein Eintrag von vor
      // diesem Deploy traegt ihn nicht und ist damit kein gueltiger Cache.
      typeof (parsed as { watchesUnassignedLeads?: unknown })
        .watchesUnassignedLeads === "boolean"
    ) {
      return parsed as AuthenticatedUser;
    }
    return null;
  } catch {
    return null;
  }
}

export function writeLastKnownUser(user: AuthenticatedUser): void {
  if (typeof window === "undefined") return;
  const stored: StoredLastKnownUser = {
    user,
    vertretenerId: readImpersonate(),
  };
  window.localStorage.setItem(KEY, JSON.stringify(stored));
}

export function clearLastKnownUser(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(KEY);
}
